import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Plus, X, Users, Share2, Trash2, Lock } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useApp } from '../context/AppContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSort, SortableTh } from '../components/SortableTable';
import TableSearch, { rowMatches } from '../components/TableSearch';
import { prettyDate as longDate } from '../lib/format';
import api from '../lib/api';

export default function AdminConsole() {
  const { isAdmin, user, authReady } = useApp();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [pendingKey, setPendingKey] = useState(null); // `${owner}::${snap}` currently being modified
  // Per-row draft for the new-grant inputs
  const [drafts, setDrafts] = useState({}); // { [`${owner}::${snap}`]: { user_id, role } }

  const { sort, onSort, sortRows } = useSort({ key: 'owner_email', dir: 'asc' });

  const load = async () => {
    setLoading(true);
    try {
      const [overview, usrs] = await Promise.all([api.adminOverview(), api.adminUsers()]);
      setRows(overview);
      setUsers(usrs);
    } catch (e) {
      toast.error(e.message || 'Could not load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authReady && isAdmin) load();
  }, [authReady, isAdmin]);

  const userByEmail = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[(u.email || '').toLowerCase()] = u; });
    return m;
  }, [users]);

  const sorted = sortRows(rows, {
    owner_email: (r) => r.owner_email || '',
    snapshot_date: (r) => r.snapshot_date || '',
    snapshot_label: (r) => r.snapshot_label || '',
    rowCount: (r) => r.rowCount || 0,
    shareCount: (r) => r.shares.length,
  });

  const filtered = sorted.filter((r) => {
    if (!query) return true;
    return rowMatches({
      owner: r.owner_email + ' ' + (r.owner_name || ''),
      snapshot: r.snapshot_label + ' ' + r.snapshot_date,
      shares: r.shares.map((s) => s.grantee_email + ' ' + s.role).join(' '),
    }, query);
  });

  const draftFor = (key) => drafts[key] || { user_id: '', role: 'view' };
  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...draftFor(key), ...patch } }));

  const addShare = async (row) => {
    const key = `${row.owner_user_id}::${row.snapshot_id}`;
    const draft = draftFor(key);
    if (!draft.user_id) {
      toast.error('Pick a user');
      return;
    }
    const grantee = users.find((u) => u.user_id === draft.user_id);
    if (!grantee) return;
    setPendingKey(key);
    try {
      await api.adminCreateShare({
        owner_user_id: row.owner_user_id,
        snapshot_id: row.snapshot_id,
        grantee_email: grantee.email,
        role: draft.role,
      });
      toast.success(`Shared ${row.snapshot_label} (${draft.role}) with ${grantee.email}`);
      setDrafts((d) => ({ ...d, [key]: { user_id: '', role: 'view' } }));
      await load();
    } catch (e) {
      toast.error(e.message || 'Share failed');
    } finally {
      setPendingKey(null);
    }
  };

  const removeShare = async (row, shareId, granteeEmail) => {
    if (!confirm(`Revoke access for ${granteeEmail} from ${row.snapshot_label}?`)) return;
    const key = `${row.owner_user_id}::${row.snapshot_id}`;
    setPendingKey(key);
    try {
      await api.adminDeleteShare(shareId);
      toast.success(`Revoked ${granteeEmail}`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Revoke failed');
    } finally {
      setPendingKey(null);
    }
  };

  const changeRole = async (row, share, newRole) => {
    const grantee = userByEmail[share.grantee_email];
    setPendingKey(`${row.owner_user_id}::${row.snapshot_id}`);
    try {
      await api.adminCreateShare({
        owner_user_id: row.owner_user_id,
        snapshot_id: row.snapshot_id,
        grantee_email: share.grantee_email,
        role: newRole,
      });
      toast.success(`${grantee?.name || share.grantee_email} → ${newRole}`);
      await load();
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setPendingKey(null);
    }
  };

  if (!authReady) return null;
  if (!isAdmin) {
    return (
      <div className="border border-border bg-white p-8 text-center" data-testid="admin-denied">
        <ShieldCheck size={28} className="text-[#AAA] mx-auto mb-3" />
        <div className="font-heading font-bold text-[16px]">Admin only</div>
        <div className="text-[12px] text-[#787872] mt-1">You don't have access to this page.</div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        overline="Admin"
        title="Admin Console"
        description={
          <>
            Manage cross-user portfolio sharing. Each row is one reporting date — pick a user and a role to grant
            <span className="chip chip-neutral ml-1 align-middle">view</span> or
            <span className="chip chip-positive ml-1 align-middle">edit</span> access. Grantees see shared
            data merged into their Dashboards with a <span className="font-semibold">shared</span> chip.
          </>
        }
        actions={
          <span className="chip chip-positive" data-testid="admin-badge">
            <ShieldCheck size={11} /> Signed in as {user?.email}
          </span>
        }
      />

      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by owner email, snapshot label, grantee email…"
        testId="admin-search"
        stats={query ? `${filtered.length} / ${rows.length} reporting dates` : `${rows.length} reporting dates across ${new Set(rows.map((r) => r.owner_user_id)).size} users`}
      />

      <div className="border border-border bg-white" data-testid="admin-overview-table">
        <table className="grid-table">
          <thead>
            <tr>
              <SortableTh sortKey="owner_email" sort={sort} onSort={onSort}>Owner</SortableTh>
              <SortableTh sortKey="snapshot_date" sort={sort} onSort={onSort} className="w-[150px]">Reporting Date</SortableTh>
              <SortableTh sortKey="snapshot_label" sort={sort} onSort={onSort} className="w-[120px]">Label</SortableTh>
              <SortableTh sortKey="rowCount" sort={sort} onSort={onSort} numeric className="w-[100px]">Rows</SortableTh>
              <th className="w-[420px]">Shared with</th>
              <th className="min-w-[300px]">Grant access</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-10 text-[#787872] text-[12px]">Loading admin overview…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-[#AAA] text-[12px]">No matching reporting dates</td></tr>
            )}
            {!loading && filtered.map((row) => {
              const key = `${row.owner_user_id}::${row.snapshot_id}`;
              const draft = draftFor(key);
              // Eligible grantees: everyone except the owner + already-granted users
              const grantedEmails = new Set(row.shares.map((s) => s.grantee_email));
              const candidates = users.filter((u) =>
                u.user_id !== row.owner_user_id && !grantedEmails.has((u.email || '').toLowerCase()),
              );
              const isPending = pendingKey === key;

              return (
                <tr key={key} data-testid={`admin-row-${row.owner_user_id}-${row.snapshot_id}`}>
                  <td>
                    <div className="font-heading font-semibold text-[13px]">{row.owner_name || row.owner_email}</div>
                    <div className="text-[10px] font-mono text-[#787872] mt-0.5">{row.owner_email}</div>
                  </td>
                  <td className="font-mono text-[12px]">
                    {row.is_locked && <Lock size={10} className="inline mr-1 text-[#787872]" />}
                    {longDate(row.snapshot_date)}
                  </td>
                  <td className="text-[12px]">{row.snapshot_label}</td>
                  <td className="num font-mono tabular-nums text-[12px]">{(row.rowCount || 0).toLocaleString('en-IN')}</td>
                  <td>
                    {row.shares.length === 0 ? (
                      <span className="text-[11px] font-mono text-[#AAA]">— Not shared</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {row.shares.map((s) => (
                          <div
                            key={s.id}
                            className="inline-flex items-center gap-1.5 border border-border bg-[#F6F6F4] pl-2 pr-1 py-0.5 rounded-sm text-[11px]"
                            data-testid={`admin-share-${s.id}`}
                          >
                            <Users size={10} className="text-[#787872]" />
                            <span className="font-mono">{s.grantee_email}</span>
                            <Select
                              value={s.role}
                              onValueChange={(v) => changeRole(row, s, v)}
                              disabled={isPending}
                            >
                              <SelectTrigger className="h-5 px-1.5 text-[10px] w-[64px] !border-none bg-transparent">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="view">view</SelectItem>
                                <SelectItem value="edit">edit</SelectItem>
                              </SelectContent>
                            </Select>
                            <button
                              onClick={() => removeShare(row, s.id, s.grantee_email)}
                              disabled={isPending}
                              className="text-[#787872] hover:text-[#9B2C2C] p-0.5"
                              title="Revoke"
                              data-testid={`admin-revoke-${s.id}`}
                            >
                              <X size={11} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {candidates.length === 0 ? (
                      <span className="text-[11px] font-mono text-[#AAA]">No eligible users left</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Select
                          value={draft.user_id}
                          onValueChange={(v) => setDraft(key, { user_id: v })}
                          disabled={isPending}
                        >
                          <SelectTrigger
                            className="h-7 text-[11px] flex-1"
                            data-testid={`admin-grant-user-${key}`}
                          >
                            <SelectValue placeholder="Pick user…" />
                          </SelectTrigger>
                          <SelectContent>
                            {candidates.map((u) => (
                              <SelectItem key={u.user_id} value={u.user_id}>
                                {u.email}{u.name ? ` (${u.name})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={draft.role}
                          onValueChange={(v) => setDraft(key, { role: v })}
                          disabled={isPending}
                        >
                          <SelectTrigger className="h-7 text-[11px] w-[72px]" data-testid={`admin-grant-role-${key}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="view">view</SelectItem>
                            <SelectItem value="edit">edit</SelectItem>
                          </SelectContent>
                        </Select>
                        <button
                          className="btn-primary !py-1 !px-2 text-[11px] disabled:opacity-50"
                          onClick={() => addShare(row)}
                          disabled={isPending || !draft.user_id}
                          data-testid={`admin-grant-add-${key}`}
                        >
                          <Plus size={11} /> Grant
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AdminListPanel />
    </>
  );
}

/** Bottom panel showing the current admin roster. */
function AdminListPanel() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { setAdmins(await api.adminListAdmins()); }
    catch (e) { toast.error(e.message || 'Could not load admins'); }
  };
  useEffect(() => { load(); }, []);

  const addAdmin = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) { toast.error('Enter a valid email'); return; }
    setLoading(true);
    try {
      await api.adminAddAdmin(trimmed);
      toast.success(`${trimmed} promoted to admin`);
      setEmail('');
      await load();
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setLoading(false); }
  };
  const removeAdmin = async (e) => {
    if (!confirm(`Remove admin rights for ${e}?`)) return;
    try {
      await api.adminRemoveAdmin(e);
      toast.success(`Removed ${e}`);
      await load();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  return (
    <section className="mt-8 border border-border bg-white" data-testid="admin-list-section">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="font-heading font-bold text-[14px] flex items-center gap-2">
            <ShieldCheck size={14} /> Admin roster
          </div>
          <div className="text-[11px] font-mono text-[#787872] mt-0.5">Anyone listed here can manage shares and add other admins.</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="new-admin@email.com"
            className="border border-border h-7 px-2 text-[12px] font-mono w-[260px]"
            data-testid="admin-add-email"
          />
          <button className="btn-primary !py-1 !px-2 text-[11px] disabled:opacity-50" onClick={addAdmin} disabled={loading} data-testid="admin-add-confirm">
            <Plus size={11} /> Add admin
          </button>
        </div>
      </div>
      <table className="grid-table" data-testid="admin-list-table">
        <thead><tr><th>Email</th><th className="w-[140px]">Added</th><th className="w-[60px]" /></tr></thead>
        <tbody>
          {admins.map((a) => (
            <tr key={a.email}>
              <td className="font-mono text-[12px]">{a.email}</td>
              <td className="font-mono text-[11px] text-[#787872]">{a.created_at ? a.created_at.slice(0, 10) : '—'}</td>
              <td>
                <button onClick={() => removeAdmin(a.email)} className="btn-ghost text-[#9B2C2C]" title="Remove admin" data-testid={`admin-remove-${a.email}`}>
                  <Trash2 size={12} />
                </button>
              </td>
            </tr>
          ))}
          {admins.length === 0 && (
            <tr><td colSpan={3} className="text-center py-6 text-[12px] text-[#AAA]">No admins yet</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
